/**
 * Chart Data Transformation Service
 *
 * This service transforms multi-dimensional survey data into Chart.js compatible data structures
 * using a flexible dimension mapping approach.
 */

import type { ChartData } from 'chart.js';

import type {
  ApiDashboardAggregate,
  DashboardResultOrgUnit,
  UpstreamApiDashboardData,
  UpstreamApiDashboardResult,
} from 'src/types/dashboard';
import i18n from 'src/services/i18n/i18next';

import type {
  ChartConfig,
  ChartJsOutput,
  CustomLabels,
  DataHandling,
  DefaultLabels,
  FallbackStrategy,
  LabelEntry,
  Labels,
} from './types';
import {
  DimensionType,
  KeyTransform,
  NullHandling,
  SortDirection,
  SortType,
} from './types';

interface FlattenedDataPoint {
  dataline: string;
  question: string;
  statistic: string;
  value: number | null;
  question_label: string;
  org_unit: Partial<DashboardResultOrgUnit>;
}

const defaultStatisticLabelKeys: Record<string, string> = {
  mean: 'Statistics.mean',
  median: 'Statistics.median',
  ptop2: 'Statistics.ptop2',
  pbottom2: 'Statistics.pbottom2',
  min: 'Statistics.min',
  max: 'Statistics.max',
  sum: 'Statistics.sum',
  std: 'Statistics.std',
  var: 'Statistics.var',
  validn: 'Statistics.validn',
};

/**
 * Transform survey data into Chart.js compatible format based on configuration.
 */
export const transformChartData = (
  surveyData: UpstreamApiDashboardData,
  config: ChartConfig,
): ChartJsOutput => {
  validateConfig(config);
  const flattenedData = extractAndFlattenData(surveyData, config);
  let chartData = buildChartData(flattenedData, config);
  chartData = addAutomaticTitle(chartData, config, flattenedData);
  return chartData;
};

const validateConfig = (config: ChartConfig): void => {
  if (config.xAxisData === config.seriesData) {
    throw new Error('xAxisData and seriesData must be different dimensions');
  }

  const allDimensions = new Set([
    DimensionType.QUESTIONS,
    DimensionType.STATISTICS,
    DimensionType.DATALINES,
  ]);
  const usedDimensions = new Set([config.xAxisData, config.seriesData]);
  const filteredDimension = Array.from(allDimensions).find(
    (dim) => !usedDimensions.has(dim),
  );
  if (!filteredDimension) {
    throw new Error('Unable to determine filtered dimension');
  }

  if (filteredDimension === DimensionType.QUESTIONS) {
    if (config.selections.questions.length > 1) {
      throw new Error(
        'When questions is the filtered dimension, select at most 1 question',
      );
    }
  } else if (filteredDimension === DimensionType.STATISTICS) {
    if (config.selections.statistics.length > 1) {
      throw new Error(
        'When statistics is the filtered dimension, select at most 1 statistic',
      );
    }
  }
};

const getFilteredDimension = (config: ChartConfig): DimensionType => {
  const allDimensions = new Set([
    DimensionType.QUESTIONS,
    DimensionType.STATISTICS,
    DimensionType.DATALINES,
  ]);
  const usedDimensions = new Set([config.xAxisData, config.seriesData]);
  const filteredDimension = Array.from(allDimensions).find(
    (dim) => !usedDimensions.has(dim),
  );
  if (!filteredDimension) {
    throw new Error('Unable to determine filtered dimension');
  }
  return filteredDimension;
};

const hasAggregateKey = (
  aggregate: ApiDashboardAggregate,
  key: string,
): key is keyof ApiDashboardAggregate => key in aggregate;

/**
 * Reads a statistic (e.g. `ptop2`, `mean`) from an aggregate by its name.
 *
 * Returns `undefined` if the aggregate does not carry that statistic at all and
 * `null` if the reporting engine delivered it empty.
 */
export const getAggregateStatisticValue = (
  aggregate: ApiDashboardAggregate,
  statistic: string,
): number | null | undefined => {
  if (!hasAggregateKey(aggregate, statistic)) {
    return undefined;
  }

  const value = aggregate[statistic];
  return typeof value === 'number' || value === null ? value : undefined;
};

const appendFlattenedAggregates = ({
  result,
  dataline,
  selections,
  flattenedData,
}: {
  result:
    | Pick<UpstreamApiDashboardResult, 'aggregates' | 'org_units'>
    | undefined;
  dataline: string;
  selections: ChartConfig['selections'];
  flattenedData: FlattenedDataPoint[];
}): void => {
  if (!result?.aggregates) {
    return;
  }

  for (const aggregate of result.aggregates) {
    if (!selections.questions.includes(aggregate.question)) {
      continue;
    }

    for (const stat of selections.statistics) {
      const value = getAggregateStatisticValue(aggregate, stat);
      if (value === undefined) {
        continue;
      }

      flattenedData.push({
        dataline,
        question: aggregate.question,
        statistic: stat,
        value,
        question_label: aggregate.label || '',
        org_unit: result.org_units?.[0] || {},
      });
    }
  }
};

const extractAndFlattenData = (
  surveyData: UpstreamApiDashboardData,
  config: ChartConfig,
): FlattenedDataPoint[] => {
  const flattenedData: FlattenedDataPoint[] = [];

  const parseIndexSelection = (
    selection: 'all' | 'none' | string[] | number[] | boolean | undefined,
    max: number,
  ): number[] => {
    if (
      selection === undefined ||
      selection === false ||
      selection === 'none'
    ) {
      return [];
    }

    if (selection === true || selection === 'all') {
      return Array.from({ length: max }, (_, i) => i);
    }

    if (Array.isArray(selection)) {
      return selection
        .map((idx) =>
          typeof idx === 'number' ? idx : parseInt(String(idx), 10),
        )
        .filter((idx) => !Number.isNaN(idx) && idx >= 0 && idx < max);
    }

    return [];
  };
  const selectedSelection = config.selections.datalines?.selected ?? 'none';

  // focal, childre and ancestor attributes in config.selections.datalines (DatalineSelection type),
  // are used to control the selection of data-lines via the Django intereface if no selected_results are given.

  // Process focal result if selected
  if (config.selections.datalines?.focal && surveyData.focal_result) {
    appendFlattenedAggregates({
      result: surveyData.focal_result,
      dataline: 'focal',
      selections: config.selections,
      flattenedData,
    });
  }

  // Process children results if selected
  if (
    config.selections.datalines?.children !== 'none' &&
    config.selections.datalines?.children !== undefined &&
    surveyData.children_results
  ) {
    let childrenToProcess: number[] = [];

    if (config.selections.datalines.children === 'all') {
      childrenToProcess = Array.from(
        { length: surveyData.children_results.length },
        (_, i) => i,
      );
    } else if (Array.isArray(config.selections.datalines.children)) {
      childrenToProcess = config.selections.datalines.children
        .map((idx) => (typeof idx === 'number' ? idx : parseInt(idx, 10)))
        .filter((idx) => !isNaN(idx));
    }

    for (const idx of childrenToProcess) {
      if (idx < surveyData.children_results.length) {
        appendFlattenedAggregates({
          result: surveyData.children_results[idx],
          dataline: `child_${idx}`,
          selections: config.selections,
          flattenedData,
        });
      }
    }
  }

  // Process ancestor result if selected
  if (config.selections.datalines?.ancestors && surveyData.ancestor_result) {
    appendFlattenedAggregates({
      result: surveyData.ancestor_result,
      dataline: 'ancestor_0',
      selections: config.selections,
      flattenedData,
    });
  }

  // Effectively if selected_results are present, then the focal, children and ancestor settings are ignored.
  // This is a soft requirement enforced by the FlexibleChartJsWidget component that calls this data provider.
  if (surveyData.selected_results) {
    const selectedIndexes = parseIndexSelection(
      selectedSelection,
      surveyData.selected_results.length,
    );

    for (const idx of selectedIndexes) {
      appendFlattenedAggregates({
        result: surveyData.selected_results[idx],
        dataline: `selected_${idx}`,
        selections: config.selections,
        flattenedData,
      });
    }
  }

  return flattenedData;
};

const buildChartData = (
  flattenedData: FlattenedDataPoint[],
  config: ChartConfig,
): ChartJsOutput => {
  const xAxisValues = getUniqueDimensionValues(flattenedData, config.xAxisData);
  const seriesValues = getUniqueDimensionValues(
    flattenedData,
    config.seriesData,
  );

  const xAxisLabels = xAxisValues.map((value) =>
    getLabel(value, config.xAxisData, config.labels, flattenedData),
  );
  const seriesLabels = seriesValues.map((value) =>
    getLabel(value, config.seriesData, config.labels, flattenedData),
  );

  const datasets = seriesValues.map((seriesValue, seriesIdx) => {
    const data = xAxisValues.map((xValue) => {
      const point = flattenedData.find((p) => {
        const xMatch = getDimensionValue(p, config.xAxisData) === xValue;
        const seriesMatch =
          getDimensionValue(p, config.seriesData) === seriesValue;
        return xMatch && seriesMatch;
      });

      if (!point || point.value === null || point.value === undefined) {
        return handleNullValue(config.dataHandling);
      }
      return point.value;
    });

    return {
      label: seriesLabels[seriesIdx],
      data: data,
    };
  });

  const sortedResult = applySorting(
    { labels: xAxisLabels, datasets: datasets },
    xAxisValues,
    seriesValues,
    flattenedData,
    config,
  );

  return {
    type: config.chartType,
    data: sortedResult,
  };
};

const getUniqueDimensionValues = (
  data: FlattenedDataPoint[],
  dimension: DimensionType,
): string[] => {
  const values = data.map((point) => getDimensionValue(point, dimension));
  return Array.from(new Set(values));
};

const getDimensionValue = (
  point: FlattenedDataPoint,
  dimension: DimensionType,
): string => {
  switch (dimension) {
    case DimensionType.QUESTIONS:
      return point.question;
    case DimensionType.STATISTICS:
      return point.statistic;
    case DimensionType.DATALINES:
      return point.dataline;
  }
};

const getLabel = (
  value: string,
  dimension: DimensionType,
  labels: Labels | undefined,
  flattenedData: FlattenedDataPoint[],
): string => {
  if (
    dimension === DimensionType.STATISTICS &&
    labels?.statisticLabels?.[value]
  ) {
    return labels.statisticLabels[value];
  }
  if (
    dimension === DimensionType.DATALINES &&
    labels?.datalineLabels?.[value]
  ) {
    return labels.datalineLabels[value];
  }

  const customLabel = getLabelFromProvider(
    value,
    dimension,
    labels?.labelProvider?.customLabels,
  );
  if (customLabel) return customLabel;

  if (dimension === DimensionType.QUESTIONS) {
    const point = flattenedData.find((p) => p.question === value);
    if (point?.question_label) {
      return point.question_label;
    }
  }

  if (dimension === DimensionType.DATALINES) {
    const point = flattenedData.find((p) => p.dataline === value);
    const orgUnitName = point?.org_unit?.name;
    if (orgUnitName) {
      return orgUnitName;
    }
  }

  const defaultLabel = getLabelFromProvider(
    value,
    dimension,
    labels?.labelProvider?.defaultLabels,
  );
  if (defaultLabel) return defaultLabel;

  if (
    dimension === DimensionType.STATISTICS &&
    defaultStatisticLabelKeys[value]
  ) {
    const translationKey = defaultStatisticLabelKeys[value];
    return i18n.t(translationKey, { ns: 'dashboard' });
  }

  return applyFallbackStrategy(value, labels?.labelProvider?.fallbackStrategy);
};

const getLabelFromProvider = (
  value: string,
  dimension: DimensionType,
  labelSet: DefaultLabels | CustomLabels | undefined,
): string | null => {
  if (!labelSet) return null;

  let labels: Record<string, LabelEntry> | undefined;
  switch (dimension) {
    case DimensionType.QUESTIONS:
      labels = labelSet.questions;
      break;
    case DimensionType.STATISTICS:
      labels = labelSet.statistics;
      break;
    case DimensionType.DATALINES:
      labels = labelSet.datalines;
      break;
  }

  if (labels?.[value]) {
    return labels[value].label;
  }

  return null;
};

const applyFallbackStrategy = (
  value: string,
  strategy: FallbackStrategy | undefined,
): string => {
  if (!strategy?.showKeys && strategy?.missingLabel) {
    return strategy.missingLabel;
  }

  switch (strategy?.keyTransform || KeyTransform.HUMANIZE) {
    case KeyTransform.HUMANIZE:
      return humanizeKey(value);
    case KeyTransform.UPPERCASE:
      return value.toUpperCase();
    case KeyTransform.NONE:
    default:
      return value;
  }
};

const humanizeKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .trim();

const handleNullValue = (
  dataHandling: DataHandling | undefined,
): number | null => {
  const nullHandling = dataHandling?.nullHandling || NullHandling.SKIP;

  switch (nullHandling) {
    case NullHandling.ZERO:
      return 0;
    case NullHandling.PLACEHOLDER:
      return typeof dataHandling?.placeholderValue === 'number'
        ? dataHandling.placeholderValue
        : null;
    case NullHandling.SKIP:
    default:
      return null;
  }
};

const applySorting = (
  chartData: ChartData,
  xAxisValues: string[],
  seriesValues: string[],
  flattenedData: FlattenedDataPoint[],
  config: ChartConfig,
): ChartData => {
  if (!config.sorting) return chartData;

  if (
    config.sorting.xAxisSort &&
    config.sorting.xAxisSort !== SortType.DEFAULT
  ) {
    const sortedIndices = getSortedIndices(
      xAxisValues,
      config.xAxisData,
      config.sorting.xAxisSort,
      config.sorting.sortDirection || SortDirection.ASC,
      config.sorting.sortByStatistic,
      flattenedData,
      config.labels,
    );

    if (chartData.labels) {
      chartData.labels = sortedIndices.map((i) => chartData.labels?.[i] ?? '');
    }
    if (chartData.datasets) {
      chartData.datasets = chartData.datasets.map((dataset) => ({
        ...dataset,
        data: sortedIndices.map((i) => (dataset.data as number[])[i]),
      }));
    }
  }

  if (
    config.sorting.seriesSort &&
    config.sorting.seriesSort !== SortType.DEFAULT
  ) {
    const sortedIndices = getSortedIndices(
      seriesValues,
      config.seriesData,
      config.sorting.seriesSort,
      config.sorting.sortDirection || SortDirection.ASC,
      config.sorting.sortByStatistic,
      flattenedData,
      config.labels,
    );

    if (chartData.datasets) {
      chartData.datasets = sortedIndices
        .map((i) => chartData.datasets?.[i])
        .filter(Boolean) as typeof chartData.datasets;
    }
  }

  return chartData;
};

const getSortedIndices = (
  values: string[],
  dimension: DimensionType,
  sortType: SortType,
  sortDirection: SortDirection,
  sortByStatistic: string | undefined,
  flattenedData: FlattenedDataPoint[],
  labels: Labels | undefined,
): number[] => {
  const indices = Array.from({ length: values.length }, (_, i) => i);

  if (sortType === SortType.ALPHABETICAL) {
    const labelsForSort = values.map((v) =>
      getLabel(v, dimension, labels, flattenedData),
    );

    indices.sort((a, b) => {
      const comparison = labelsForSort[a].localeCompare(labelsForSort[b]);
      return sortDirection === SortDirection.DESC ? -comparison : comparison;
    });
  } else if (sortType === SortType.VALUE && sortByStatistic) {
    const valuesForSort = values.map((v) => {
      const relevantPoints = flattenedData.filter(
        (p) =>
          getDimensionValue(p, dimension) === v &&
          p.statistic === sortByStatistic,
      );
      if (relevantPoints.length === 0) return 0;

      const sum = relevantPoints.reduce((acc, p) => acc + (p.value || 0), 0);
      return sum / relevantPoints.length;
    });

    indices.sort((a, b) => {
      const comparison = valuesForSort[a] - valuesForSort[b];
      return sortDirection === SortDirection.DESC ? -comparison : comparison;
    });
  }

  return indices;
};

const addAutomaticTitle = (
  chartData: ChartJsOutput,
  config: ChartConfig,
  flattenedData: FlattenedDataPoint[],
): ChartJsOutput => {
  const filteredDimension = getFilteredDimension(config);
  const title = getTitleForFilteredDimension(
    filteredDimension,
    config,
    flattenedData,
  );

  if (title) {
    const existingOptions = chartData.options || {};
    const existingPlugins = existingOptions.plugins || {};

    chartData.options = {
      ...existingOptions,
      plugins: {
        ...existingPlugins,
        title: {
          ...(existingPlugins.title || {}),
          display: true,
          text: title,
        },
      },
    };
  }

  return chartData;
};

const getTitleForFilteredDimension = (
  dimension: DimensionType,
  config: ChartConfig,
  flattenedData: FlattenedDataPoint[],
): string | undefined => {
  let selectedValues: string[] = [];

  switch (dimension) {
    case DimensionType.QUESTIONS:
      selectedValues = config.selections.questions;
      break;
    case DimensionType.STATISTICS:
      selectedValues = config.selections.statistics;
      break;
    case DimensionType.DATALINES:
      selectedValues = getUniqueDimensionValues(
        flattenedData,
        DimensionType.DATALINES,
      );
      break;
  }

  if (!selectedValues.length) {
    return undefined;
  }

  const uniqueLabels = Array.from(
    new Set(
      selectedValues.map((value) =>
        getLabel(value, dimension, config.labels, flattenedData),
      ),
    ),
  );

  // If only a single dataline is filtered out, skip the title to avoid
  // low-value labels like "Focal" while keeping legend/x-axis uncluttered.
  if (dimension === DimensionType.DATALINES && uniqueLabels.length <= 1) {
    return undefined;
  }

  return uniqueLabels.join(', ');
};
